import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { PackageBenefitsSummary } from "@whoosh/features/src/Packages";

import { PackageItemActions } from "./PackageItemActions";
import { PackagePriceDisplay } from "./PackagePriceDisplay";
import { StoreProductPackageItem_package$key } from "./__generated__/StoreProductPackageItem_package.graphql";
import { StoreProductPackageItem_session$key } from "./__generated__/StoreProductPackageItem_session.graphql";

type Props = {
  packageRef?: StoreProductPackageItem_package$key | null;
  sessionRef?: StoreProductPackageItem_session$key | null;
  openPackageModalOnLoad?: boolean;
  onPackageAuthComplete?: () => void;
};

export const StoreProductPackageItem = ({
  packageRef,
  sessionRef,
  openPackageModalOnLoad,
  onPackageAuthComplete,
}: Props) => {
  const pkg = useFragment(StoreProductPackageItemFragmentString, packageRef);
  const session = useFragment(
    StoreProductPackageItemSessionFragmentString,
    sessionRef
  );

  const isMembership = pkg?.__typename === "MembershipPackage";

  const activePackages =
    session?.currentClientProfile?.member?.activePackages?.edges?.flatMap((p) =>
      p?.node ? [p.node] : []
    ) ?? [];

  const upcomingPackages =
    session?.currentClientProfile?.member?.upcomingPackages?.edges?.flatMap(
      (p) => (p?.node ? [p.node] : [])
    ) ?? [];

  const currentCustomerHasPackage = [
    ...activePackages,
    ...upcomingPackages,
  ].some((memberPackage) => memberPackage.package?.id === pkg?.id);

  return pkg ? (
    <VStack
      p={2}
      backgroundColor={
        currentCustomerHasPackage && isMembership
          ? "surface-main-primary-subtlest"
          : "surface-secondary"
      }
      borderRadius="2"
      flexGrow={1}
      maxWidth={{ base: "100%", lg: 344 }}
    >
      <VStack gap={1} px={4} pt={3} pb={5}>
        <Text variant="mainHeading">
          {pkg.displayInApp?.displayName ?? pkg.name}
        </Text>
        <PackagePriceDisplay packageRef={pkg} />
      </VStack>
      <VStack bg="surface-primary" borderRadius="2" p={5} gap={4} flex={1}>
        <PackageBenefitsSummary
          packageRef={pkg}
          hideTitle
          maxInitialDisplay={6}
        />
        <Box flex={1} />
        <PackageItemActions
          packageRef={pkg}
          sessionRef={session}
          customerHasPackage={currentCustomerHasPackage}
          openPackageModalOnLoad={openPackageModalOnLoad}
          onPackageAuthComplete={onPackageAuthComplete}
        />
      </VStack>
    </VStack>
  ) : null;
};

const StoreProductPackageItemFragmentString = graphql`
  fragment StoreProductPackageItem_package on Package {
    id
    __typename
    name
    displayInApp {
      displayName
    }
    ...PackagePriceDisplay_package
    ...PackageBenefitsSummary_package
    ...PackageItemActions_package
  }
`;

const StoreProductPackageItemSessionFragmentString = graphql`
  fragment StoreProductPackageItem_session on Session {
    ...PackageItemActions_session
    currentClientProfile {
      member {
        ... on Customer {
          activePackages: memberPackages(status: ACTIVE) {
            edges {
              node {
                id
                package {
                  id
                }
              }
            }
          }
          upcomingPackages: memberPackages(status: UPCOMING) {
            edges {
              node {
                id
                package {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;
